import {
  Icons,
  LabelChip,
  Tooltip,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';

import {
  OptimizationType,
  PreparedWorkload,
} from 'features/workload-optimization';

export const OptimizationTypeCell = ({
  optimizationTypeState,
}: PreparedWorkload) => {
  const vEnabled =
    optimizationTypeState.type === OptimizationType.BOTH ||
    optimizationTypeState.type === OptimizationType.VERTICAL;
  const hEnabled =
    optimizationTypeState.type === OptimizationType.BOTH ||
    optimizationTypeState.type === OptimizationType.HORIZONTAL;

  const tooltipContent = (
    <Stack gap={8}>
      {optimizationTypeState.isOverridden ? (
        <Stack direction="row" gap={6} alignItems="center">
          <Icons.CastHorizontalLinkDash
            size={16}
            color={tokens.theme.fg.structural.secondary}
          />
          <Typography
            variant="P12R"
            color={tokens.theme.fg.structural.primaryOnInverse}
          >
            Overrides policy
          </Typography>
        </Stack>
      ) : (
        <Typography
          variant="P12R"
          color={tokens.theme.fg.structural.primaryOnInverse}
        >
          Follows policy
        </Typography>
      )}
      <Stack gap={4}>
        <Stack direction="row" gap={4} alignItems="baseline">
          <LabelChip
            size="xs"
            sx={{
              bgcolor: vEnabled
                ? tokens.theme.bg.status.infoSubtle
                : tokens.theme.bg.status.neutralSubtle,
              color: vEnabled
                ? tokens.theme.fg.status.onInfoSubtle
                : tokens.theme.fg.status.onNeutralSubtle,
              textTransform: 'none',
              py: 4,
            }}
          >
            V
          </LabelChip>
          <Typography
            variant="P12B"
            color={tokens.theme.fg.structural.primaryOnInverse}
          >
            Vertical:{' '}
            <Box
              component="span"
              sx={{
                color: vEnabled
                  ? tokens.theme.fg.status.onInfoSubtle
                  : 'inherit',
              }}
            >
              {vEnabled ? 'ON' : 'OFF'}
            </Box>
          </Typography>
        </Stack>
        <Stack direction="row" gap={4} alignItems="baseline">
          <LabelChip
            size="xs"
            sx={{
              bgcolor: hEnabled
                ? tokens.theme.bg.status.infoSubtle
                : tokens.theme.bg.status.neutralSubtle,
              color: hEnabled
                ? tokens.theme.fg.status.onInfoSubtle
                : tokens.theme.fg.status.onNeutralSubtle,
              textTransform: 'none',
            }}
          >
            H
          </LabelChip>
          <Typography
            variant="P12B"
            color={tokens.theme.fg.structural.primaryOnInverse}
          >
            Horizontal:{' '}
            <Box
              component="span"
              sx={{
                color: hEnabled
                  ? tokens.theme.fg.status.onInfoSubtle
                  : 'inherit',
              }}
            >
              {hEnabled ? 'ON' : 'OFF'}
            </Box>
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );

  return (
    <Tooltip title={tooltipContent} placement="left" sx={{ minWidth: 214 }}>
      <Stack direction="row" gap={4} alignItems="center">
        <LabelChip
          testId="optimization-type-v-badge"
          size="xs"
          sx={{
            bgcolor: vEnabled
              ? tokens.theme.bg.status.infoSubtle
              : tokens.theme.bg.status.neutralSubtle,
            color: vEnabled
              ? tokens.theme.fg.status.onInfoSubtle
              : tokens.theme.fg.status.onNeutralSubtle,
            textTransform: 'none',
            py: 4,
          }}
        >
          V
        </LabelChip>
        <LabelChip
          testId="optimization-type-h-badge"
          size="xs"
          sx={{
            bgcolor: hEnabled
              ? tokens.theme.bg.status.infoSubtle
              : tokens.theme.bg.status.neutralSubtle,
            color: hEnabled
              ? tokens.theme.fg.status.onInfoSubtle
              : tokens.theme.fg.status.onNeutralSubtle,
            textTransform: 'none',
          }}
        >
          H
        </LabelChip>
        {optimizationTypeState.isOverridden && (
          <Icons.CastHorizontalLinkDash
            data-testid="optimization-type-overridden-icon"
            size={16}
            color={tokens.theme.fg.structural.secondary}
          />
        )}
      </Stack>
    </Tooltip>
  );
};
